import Bento from "@/components/molecules/bento";
import { appLocales, Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { Simplify } from "@/prismicio-types";
import { Content, GroupField } from "@prismicio/client";
import { SliceComponentProps } from "@prismicio/react";

export type SimilarPacksProps = SliceComponentProps<Content.SimilarPacksSlice> & {
  context?: {
    locale?: Locales;
  };
};

const groupPacks = (similarPacks: GroupField<Simplify<Content.SimilarPacksSliceDefaultPrimarySimilarPacksItem>>) => {
  const groupedPacks = [];
  for (let i = 0; i < similarPacks.length; i += 4) {
    groupedPacks.push(similarPacks.slice(i, i + 4));
  }
  return groupedPacks;
};

const renderBentoItem = (
  pack: Simplify<Content.SimilarPacksSliceDefaultPrimarySimilarPacksItem>,
  variant: "lg" | "md" | "sm",
  index: number,
  locale: Locales
) => {
  return (
    <Bento
      key={`${variant}-${index}`}
      creditState={pack.credit_photo_etat_de_la_boite === "closed" ? "default" : "open"}
      variant={variant}
      locale={locale}
      link={pack.link}
      photographer={{
        name: pack.photographer_name!,
        avatar: pack.photographer_image.url || "",
        description: pack.citation!,
      }}
      image={{
        src: pack.image.url || "",
        alt: pack.image.alt || "",
      }}
      button={{ text: __("See used items", locale), link: pack.link }}
      priority={index === 0}
    />
  );
};

const SimilarPacks = ({ slice, context }: SimilarPacksProps) => {
  const locale = context?.locale || appLocales.defaultLocale;
  const { title, similar_packs } = slice.primary;

  if (!similar_packs || similar_packs.length === 0) {
    return null;
  }

  const groupedPacks = groupPacks(similar_packs); // Group into sets of 4 to display in a grid layout

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="container my-8 space-y-7.5"
    >
      <h2 className="font-cabinet-grotesk text-center text-3xl font-medium">{title}</h2>

      <div className="container px-4 md:px-7.5">
        {groupedPacks.map((group, groupIndex) => (
          <div key={groupIndex} className="mb-2.5 grid grid-cols-1 gap-2.5 xl:grid-cols-2">
            {/* First column - Large variant */}
            {group[0] && renderBentoItem(group[0], "lg", groupIndex * 4, locale)}

            {/* Second column - Medium + Small variants */}
            <div className="grid gap-2.5">
              {/* First row - Medium variant */}
              {group[1] && renderBentoItem(group[1], "md", groupIndex * 4 + 1, locale)}

              {/* Second row - Two small variants side by side */}
              <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
                {group[2] && renderBentoItem(group[2], "sm", groupIndex * 4 + 2, locale)}
                {group[3] && renderBentoItem(group[3], "sm", groupIndex * 4 + 3, locale)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default SimilarPacks;
